import { useCallback, useEffect, useRef } from "react";

/**
 * useLivePolling — silent background refresher for a load function.
 *
 *  - Calls `loadFn` every `intervalMs` (default 30 s).
 *  - Pauses while the tab is hidden (visibilitychange) and immediately
 *    re-fires on focus so the user always sees fresh data when they
 *    come back.
 *  - Skips the first poll if `skipInitial` is true (caller already did
 *    a manual load). When `skipInitial` is false, that initial call
 *    fires unconditionally (even if the tab happens to be hidden at
 *    mount) — matching the historical "fetch immediately on mount"
 *    behavior of the pollers this hook replaces.
 *  - Never starts a new call while the previous one for this same
 *    poller instance is still pending (in-flight guard) — a slow
 *    response can't cause overlapping requests to pile up.
 *  - Returns a stable `refresh()` callback external code can call to
 *    trigger an immediate poll (e.g. in response to a custom "refresh
 *    now" event) that still goes through the same in-flight guard —
 *    calling loadFn directly instead would bypass it.
 *
 *  loadFn receives one argument, `isCancelled()` — a function that
 *  returns true once this poller instance has unmounted (or is about to
 *  re-subscribe because a dep changed). Because the interval/visibility
 *  machinery here can only stop *future* calls, not a request already
 *  in flight, any loadFn that produces a visible side effect (pushing a
 *  toast, playing a sound, etc.) from data fetched after an `await`
 *  MUST check `isCancelled()` right after that await and skip applying
 *  the result if it's true — otherwise a response that resolves after
 *  unmount can still show stale data (e.g. a toast surfacing on a route
 *  that should never see it). Purely updating local component state via
 *  setState is safe either way — React 18 silently ignores state updates
 *  on unmounted components.
 *
 *  This is the project-wide auto-refresh primitive used by Agenda,
 *  Reservaciones, Alertas Operativas, Notificaciones, Chat, etc. Each
 *  module remains tenant-scoped (the loadFn already hits the
 *  tenant-filtered endpoint) so polling never crosses tenant boundaries.
 */
export default function useLivePolling(loadFn, intervalMs = 30000, deps = [], { skipInitial = true } = {}) {
  const fnRef = useRef(loadFn);
  fnRef.current = loadFn;
  const inFlightRef = useRef(false);
  const runRef = useRef(() => {});

  useEffect(() => {
    let cancelled = false;
    let id = null;
    const isCancelled = () => cancelled;

    // Runs loadFn, guarding against overlap with a still-pending previous
    // call. Does NOT by itself prevent loadFn from applying a late result
    // after cancellation — see isCancelled() note in the hook's docstring.
    const run = async () => {
      if (cancelled) return;
      if (inFlightRef.current) return; // previous call still pending — skip this one
      inFlightRef.current = true;
      try {
        await fnRef.current?.(isCancelled);
      } catch {
        /* swallow */
      } finally {
        inFlightRef.current = false;
      }
    };
    runRef.current = run;

    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      run();
    };

    if (!skipInitial) {
      run();
    }

    id = setInterval(tick, intervalMs);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Fire immediately on focus so user sees fresh data
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      runRef.current = () => {};
      if (id) clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);

  return useCallback(() => runRef.current(), []);
}
